/**
 * @file Alerte d'invitation à réserver par téléphone (préavis minimum dépassé).
 * @module packages.common.components.table-reservation.components.NoticeClosedAlert
 *
 * Affichée à l'étape des horaires — et uniquement là — quand la date choisie
 * n'est plus réservable en ligne à cause du préavis minimum
 * (`bookingPolicy.noticeClosedWarning`) : message personnalisable par
 * l'établissement (sinon texte standard traduit) suivi du téléphone en lien
 * `tel:` (appui = appel direct sur mobile).
 */

import React from 'react';
import PropTypes from 'prop-types';

// @mui
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';

// icons
import PhoneIcon from '@mui/icons-material/Phone';

// utils
import {formatPhoneNumber} from 'packages/common/utils/formatPhoneNumber';

// Repli ultime, utilisé seulement si `t` est indisponible. Doit rester identique au
// texte i18n `widgets.common.noticeClosedDefault` (locales/langs/fr.json), qui est la
// source de vérité de l'affichage client, et au placeholder du dashboard.
const DEFAULT_MESSAGE_FR = 'Les réservations en ligne ne sont plus possibles pour cette date. Contactez-nous par téléphone pour réserver.';

/**
 * Alerte « réservez par téléphone » avec message personnalisable et lien d'appel.
 *
 * @param {Object} props
 * @param {Function} [props.getCustomText] - Résolution des textes personnalisés de l'établissement
 * @param {Function} [props.t] - Fonction de traduction générique
 * @param {{phonePrefix: (string|null), phoneNumber: string}} [props.phone] - Téléphone
 *   structuré de l'établissement (`contactPhone` de la config publique)
 * @param {Object} [props.sx] - Styles MUI additionnels
 * @return {JSX.Element}
 */
export default function NoticeClosedAlert({getCustomText, t, phone, sx}) {
  const customMessage = getCustomText ? getCustomText('noticeClosedMessage') : '';
  const message = customMessage || (t ? t('widgets.common.noticeClosedDefault') : DEFAULT_MESSAGE_FR);

  // Le téléphone en lien cliquable n'est ajouté qu'au message standard : dès que le
  // restaurateur saisit son propre message, il en maîtrise entièrement le contenu
  // (numéro et formulation compris) et rien n'est accolé automatiquement.
  // Affichage par pays via le formateur canonique (libphonenumber) ; repli sur le
  // numéro brut si le préfixe manque (numéro stocké déjà en international).
  const phoneDisplay = !customMessage && phone ?
    formatPhoneNumber(phone.phonePrefix, phone.phoneNumber) || phone.phoneNumber || '' :
    '';

  return (
    <Alert
      severity="warning"
      icon={<PhoneIcon fontSize="small" />}
      role="status"
      sx={sx}
    >
      {message}
      {phoneDisplay && (
        <>
          {' '}
          <Link href={`tel:${phoneDisplay.replace(/\s/g, '')}`} sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>
            {phoneDisplay}
          </Link>
        </>
      )}
    </Alert>
  );
}

NoticeClosedAlert.propTypes = {
  getCustomText: PropTypes.func,
  t: PropTypes.func,
  phone: PropTypes.shape({
    phonePrefix: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  sx: PropTypes.object,
};
